import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DialogService } from '../dialog';
import { saveAs } from 'file-saver';
import { NGXLogger } from 'ngx-logger';
import { filter } from 'rxjs';
import { SimpleDialogComponent } from 'src/app/components/simple-dialog/simple-dialog.component';
import { Analytics } from 'src/app/services/analytics';
import { PdfService } from 'src/app/services/pdf.service';
import { QrCodeService } from 'src/app/services/qr-code.service';
import { SignaturesService } from 'src/app/services/signatures.service';
import { QR_CODE_NOT_FOUND } from 'src/app/shared/constants/errors.constants';
import * as File from 'src/app/shared/model/file.model';
import { FileComparisonDialogComponent } from '../file-comparison-dialog/file-comparison-dialog.component';
import { QrCodeSecretDialogComponent } from '../qr-code-secret-dialog/qr-code-secret-dialog.component';
import { StatefulLoadingComponent } from '../stateful-loading/stateful-loading.component';
import { ValidationRetryDialogComponent } from '../validation-retry-dialog/validation-retry-dialog.component';

@Component({
  selector: 'app-file-preview',
  templateUrl: './file-preview.component.html',
  styleUrls: ['./file-preview.component.scss'],
})
export class FilePreviewComponent {
  @Input()
  status: string | null = null;

  @Input()
  signerName: string | null = '';

  @Input()
  signerCPF: string | null = '';

  @Input()
  signerCNPJ: string | null = '';

  @Input()
  showSignaturePopover = false;

  @Input()
  showQrCodeButton = false;

  @Input() showDelete = false;

  @Output() delete = new EventEmitter<number>();

  zoom = 1.0;
  fileContents!: {
    password?: string;
    url?: string;
    data?: Uint8Array;
  };
  loading = true;
  errorMessage: string | null = null;
  focused = false;
  rotation = 0;
  qrCodeDialogIsOpen = false;

  private _fileName!: string;
  private _fileControl!: File.Control;
  private _fileType!: string;
  private _pdfPassword!: string;

  constructor(
    private logger: NGXLogger,
    private dialog: DialogService,
    private analytics: Analytics,
    private qrCodeSecretDialog: DialogService,
    private fileComparisonDialog: DialogService,
    private qrCodeService: QrCodeService,
    private pdfService: PdfService,
    private qrCodeLoadingDialog: DialogService,
    private signaturesService: SignaturesService
  ) {}

  get fileName(): string {
    return this._fileName;
  }
  @Input() set fileName(fileName: string) {
    this._fileName = fileName;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get fileType(): string {
    return this._fileType;
  }
  @Input() set fileType(fileType: string) {
    this._fileType = fileType;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get fileControl(): File.Control {
    return this._fileControl;
  }
  @Input() set fileControl(fileControl: File.Control) {
    this._fileControl = fileControl;
  }

  @Input() set pdfPassword(pwd: string) {
    this._pdfPassword = pwd;
    this.fileContents = {
      url: this.fileContents?.url,
      password: pwd,
    };
  }

  @Input() set fileSrc(fileSrc: string) {
    this.startLoad();
    this.resetFile(!!fileSrc);

    if (!!fileSrc) {
      this.fileContents = {
        url: fileSrc,
        password: this._pdfPassword,
      };
    }
  }

  @Input() set fileInputed(fileInputed: File) {
    this.startLoad();
    this.resetFile(!!fileInputed);

    if (!!fileInputed) {
      const reader = new FileReader();

      reader.addEventListener(
        'load',
        () => {
          this.fileContents = {
            data: new Uint8Array(reader.result as ArrayBuffer),
            password: this.pdfPassword,
          };
        },
        false
      );

      reader.readAsArrayBuffer(fileInputed);
    }
  }

  scanQrCode() {
    StatefulLoadingComponent.open(
      this.qrCodeLoadingDialog,
      {
        title: 'Por favor, aguarde.',
        content: 'Estamos lendo o QR code.',
      },
      {
        width: '450px',
        disableClose: true,
      }
    );

    if (this.fileContents?.url) {
      const parts = this.fileContents.url.split('/');
      if (parts.length > 6) {
        const bucket = parts[3];
        const companyId = parts[4];
        const sickNote = parts[5];
        const fileName_ = parts[6];
        const fileName = fileName_.includes('?')
          ? fileName_.split('?')[0]
          : fileName_;
        const path = [companyId, sickNote, fileName].join('/');
        this.qrCodeDialogIsOpen = true;
        this.qrCodeService.findQrCode(bucket, path).subscribe({
          next: (response) => {
            if (response.data.need_secret) {
              this.qrCodeLoadingDialog.closeAll();
              interface QrCodeDialogResult {
                url?: string;
                status?: string;
                secret?: string;
              }

              const dialogRef = this.qrCodeSecretDialog.open<
                QrCodeSecretDialogComponent,
                unknown,
                QrCodeDialogResult | null
              >(QrCodeSecretDialogComponent, {
                data: {
                  originalImageUrl: this.fileContents.url,
                  qrCodeLink: response.link,
                },
                width: '340px',
                hasBackdrop: false,
                panelClass: 'qr-code-container',
              });

              dialogRef.afterClosed().subscribe((dialogData) => {
                this.qrCodeDialogIsOpen = false;
                if (dialogData?.url && dialogData.status) {
                  this.openFileComparisonDialog(
                    dialogData.url,
                    dialogData.status
                  );
                } else {
                  ValidationRetryDialogComponent.open(
                    this.dialog,
                    {
                      title: 'Algo deu errado!',
                      titleStyle: { color: '#D80000' },
                      content:
                        'Ocorreu um erro inesperado. Por favor, tente realizar a ação novamente.',
                      yesButton: 'TENTAR NOVAMENTE',
                      noButton: 'CANCELAR',
                      url: response.link,
                      secret: dialogData.secret,
                    },
                    {
                      width: '450px',
                      panelClass: 'download-or-validate-error',
                    }
                  )
                    .afterClosed()
                    .pipe(filter((result) => !!result))
                    .subscribe((result) =>
                      this.openFileComparisonDialog(
                        result?.url ?? '',
                        result?.status ?? ''
                      )
                    );
                }
              });
            } else {
              this.signaturesService.validade(response.link).subscribe({
                next: (result) => {
                  this.qrCodeLoadingDialog.closeAll();
                  this.openFileComparisonDialog(response.link, result.status);
                },
                error: () => {
                  this.qrCodeLoadingDialog.closeAll();
                  SimpleDialogComponent.open(
                    this.dialog,
                    {
                      title: 'Algo deu errado!',
                      content:
                        'Ocorreu um erro inesperado. Por favor, tente realizar a ação novamente.',
                      titleStyle: { color: '#D80000' },
                      noButton: 'FECHAR',
                    },
                    {
                      width: '450px',
                    }
                  );
                },
              });
            }
          },
          error: (error) => {
            this.qrCodeLoadingDialog.closeAll();
            let title: string;
            let content: string;
            if (error.status === QR_CODE_NOT_FOUND) {
              title = 'Não foi possível ler o QR Code!';
              content =
                'Verifique se o documento possui um QRcode e certifique-se de que nenhuma parte dele esteja cortada ou danificada.';
            } else {
              title = 'Algo deu errado!';
              content =
                'Ocorreu um erro inesperado. Por favor, tente realizar a ação novamente.';
            }
            SimpleDialogComponent.open(
              this.dialog,
              {
                title,
                content,
                titleStyle: { color: '#D80000' },
                noButton: 'FECHAR',
              },
              {
                width: '450px',
              }
            );
            this.qrCodeDialogIsOpen = false;
          },
        });
      }
    }
  }

  openFileComparisonDialog(url: string, status: string) {
    this.fileComparisonDialog.open(FileComparisonDialogComponent, {
      data: {
        originalFile: {
          url: this.fileContents?.url,
          name: this._fileName,
          type: this._fileType,
        },
        downloadedFile: {
          url,
          name: this._fileName,
          type: 'application/pdf',
        },
        status,
        signerName: this.signerName,
        signerCPF: this.signerCPF,
        signerCNPJ: this.signerCNPJ,
      },
      backdropClass: 'file-comparison-dialog-backdrop',
      panelClass: 'file-comparison-dialog-panel',
      maxWidth: '100vw',
      maxHeight: '100vh',
    });
  }

  download() {
    SimpleDialogComponent.open(
      this.dialog,
      {
        title: 'Baixar arquivo',
        content:
          'Ao baixar o arquivo para o seu computador, você aumenta o risco de exposição de dados sensíveis. Deseja continuar?',
        yesButton: 'Sim, baixar',
        noButton: 'Cancelar',
      },
      {
        width: '500px',
      }
    )
      .afterClosed()
      .subscribe((result) => {
        if (result?.action) {
          saveAs(
            this.fileContents?.url ?? '',
            `${this.fileName}.${this.fileType.split('/')[1]}`
          );
        }
      });
  }

  zoomIn() {
    this.zoom = Math.min(5.0, this.zoom + 0.1);
  }

  zoomOut() {
    this.zoom = Math.max(1.0, this.zoom - 0.1);
  }

  defaultZoom() {
    this.zoom = 1.0;
  }

  rotate(): void {
    this.rotation = (this.rotation + 90) % 360;
  }

  errorOnLoad(error: any): void {
    const message = `Error loading pdf: ${error?.message}`;
    console.error(error);

    this.loading = false;
    if (error?.name === 'PasswordException') {
      this.errorMessage =
        'O PDF está protegido com senha e não pode ser exibido. Solicite para o colaborador reenviar um arquivo sem senha.';
      // TODO try to get password?
    } else if (!error?.message?.includes('Worker was destroyed')) {
      this.errorMessage =
        'Ocorreu um erro ao visualizar o arquivo. Tente atualizar a página ou fale com o suporte no botão de ajuda.';
      this.logger.error(JSON.stringify(error));
      this.analytics.error(this.fileContents?.url ?? '', message);
    }
  }

  private startLoad(): void {
    this.errorMessage = null;

    this.loading = true;
  }

  private resetFile(hasFiles: boolean): void {
    if (hasFiles) {
      this.rotation = 0;
      this.zoom = 1;
    }
  }
}
